import { AmountUnit, DashboardRole, DashboardViewId } from './finalmimasdashboard.models';

/*
 * Final MiMaS dashboard constants.
 *
 * Two kinds of values live here:
 *  1. Real constants used in logic (status codes, unit factors, page sizes, delays).
 *  2. FALLBACK_* values: the dashboard's roles, view access, districts, status labels and
 *     mineral units. There is no backend config API, so these are the only source.
 */

// ---------------------------------------------------------------------------
// Units and number formatting
// ---------------------------------------------------------------------------
export const LAKH = 100000;
export const CRORE = 10000000;

export const AMOUNT_UNIT_FACTOR: { [unit in AmountUnit]: number } = {
  rupees: 1,
  lakh: LAKH,
  crore: CRORE
};

/**
 * Legacy heuristic (fallback only): values below this were assumed to be in lakh/crore
 * rather than rupees. Used only when the config API does not declare the unit.
 */
export const LEGACY_SMALL_AMOUNT_THRESHOLD = 1000;
/** Legacy heuristic (fallback only): production values at or below this were assumed to be in lakh MT. */
export const LEGACY_LAKH_MT_THRESHOLD = 100;
/** Legacy heuristic (fallback only): revenue numbers above this were assumed to be in rupees, not crore. */
export const LEGACY_RUPEE_REVENUE_THRESHOLD = 100000000;

// ---------------------------------------------------------------------------
// Status codes (must match the backend)
// ---------------------------------------------------------------------------
export const APPLICATION_STATUS = {
  IN_PROCESS: '0',
  APPROVED: '1',
  REJECTED: '2',
  REJECTED_ALT: '21',
  SUSPENDED: '3',
  WITHDRAWN: '4'
} as const;

export const FALLBACK_APPLICATION_STATUS_LABELS: { [code: string]: string } = {
  [APPLICATION_STATUS.APPROVED]: 'Approved',
  [APPLICATION_STATUS.IN_PROCESS]: 'In Process',
  [APPLICATION_STATUS.REJECTED]: 'Rejected',
  [APPLICATION_STATUS.REJECTED_ALT]: 'Rejected',
  [APPLICATION_STATUS.SUSPENDED]: 'Suspended',
  [APPLICATION_STATUS.WITHDRAWN]: 'Withdrawn'
};

/** Device / vehicle flags sent by the VTS APIs as 1 (on) or 0 (off). */
export const DEVICE_FLAG_ON = 1;

// ---------------------------------------------------------------------------
// Chart.js 4 bundle built from npm by angular.json ("bundleName": "chartjs4", "inject": false)
// ---------------------------------------------------------------------------
export const CHART_JS_BUNDLE = 'chartjs4.js';

// ---------------------------------------------------------------------------
// Paging, timing
// ---------------------------------------------------------------------------
export const DEFAULT_PAGE_SIZE = 10;
export const ENFORCEMENT_DRILL_PAGE_SIZE = 25;

export const CLOCK_TICK_MS = 1000;
/** Delay before drawing charts into freshly injected HTML. */
export const CHART_RENDER_DELAY_MS = 60;
export const CHART_RETRY_DELAY_MS = 80;
export const CHART_RETRY_COUNT = 3;
export const TOAST_DURATION_MS = 2600;
export const SEARCH_DEBOUNCE_MS = 300;

// ---------------------------------------------------------------------------
// Financial year helpers (fallback when the config API is unavailable)
// ---------------------------------------------------------------------------
/** Indian financial year starts in April (month index 3). */
export const FY_START_MONTH_INDEX = 3;
export const FALLBACK_FY_COUNT = 5;

/** Returns the financial year for a date in 'YYYY-YYYY' format, e.g. '2026-2027'. */
export function financialYearFor(date: Date = new Date()): string {
  const start = date.getMonth() >= FY_START_MONTH_INDEX ? date.getFullYear() : date.getFullYear() - 1;
  return `${start}-${start + 1}`;
}

/** Current and previous financial years, newest first. */
export function recentFinancialYears(count: number = FALLBACK_FY_COUNT, date: Date = new Date()): string[] {
  const startYear = Number(financialYearFor(date).split('-')[0]);
  return Array.from({ length: count }, (_, i) => `${startYear - i}-${startYear - i + 1}`);
}

// ---------------------------------------------------------------------------
// FALLBACK: role views (config API: roles[], user.allowedViews)
// Officials' names are intentionally blank: they must come from roles[].personName.
// ---------------------------------------------------------------------------
export const FALLBACK_ROLES: DashboardRole[] = [
  { id: "minister", name: "Minister", icon: "👑", person: "", title: "Hon'ble Minister for Mines & Mineral Resources", init: "TP" },
  { id: "acs", name: "Addl. Chief Secretary", icon: "🏢", person: "", title: "Additional Chief Secretary, Industries Dept.", init: "SC" },
  { id: "director", name: "Director HQ", icon: "⭐", person: "", title: "Director, Geology & Mining", init: "TP" },
  { id: "district", name: "District Officer", icon: "📍", person: "District Officer", title: "Dy. Director — District Office", init: "DO" },
  { id: "lease", name: "Lease & Permit Officer", icon: "📋", person: "Dealing Officer", title: "Dealing Officer — Concessions", init: "LO" },
  { id: "weighbridge", name: "Weighbridge Operator", icon: "⚖️", person: "WB Operator", title: "Weighbridge Station", init: "WO" },
  { id: "enforcement", name: "Enforcement Officer", icon: "🛡️", person: "Enforcement Officer", title: "Vigilance & Flying Squad", init: "EO" },
  { id: "vts", name: "VTS Command Centre", icon: "📡", person: "VTS Controller", title: "VTS Control Room", init: "VC" },
  { id: "lessee", name: "Lessee Portal", icon: "👥", person: "Lease Holder", title: "Lessee Portal", init: "LP" }
];
/** Numeric role IDs from the user session that see every executive view. */
export const FALLBACK_ADMIN_ROLE_IDS = [1, 2, 4, 999];
/** Role-name keywords (whole words) that also grant every view. */
export const FALLBACK_ADMIN_ROLE_KEYWORDS = ['admin', 'super admin', 'superadmin'];

/** Session role IDs and whole-word role-name keywords that map to each dashboard view. */
export const FALLBACK_VIEW_ACCESS: Array<{ view: DashboardViewId; roleIds: number[]; keywords: string[] }> = [
  { view: 'minister', roleIds: [], keywords: ['minister'] },
  { view: 'acs', roleIds: [], keywords: ['additional chief secretary', 'chief secretary', 'acs'] },
  { view: 'director', roleIds: [135, 136, 137, 141, 145], keywords: ['additional director', 'director', 'cgm', 'commissioner'] },
  { view: 'district', roleIds: [100, 101, 102], keywords: ['district', 'collector', 'dy. director', 'dd', 'assistant director'] },
  { view: 'lease', roleIds: [104, 105, 111, 150, 180], keywords: ['lease', 'permit', 'concession'] },
  { view: 'weighbridge', roleIds: [9, 9.1, 9.2], keywords: ['weighbridge', 'wb'] },
  { view: 'enforcement', roleIds: [7, 8], keywords: ['enforcement', 'flying squad', 'vigilance'] },
  { view: 'vts', roleIds: [500, 510], keywords: ['vts', 'vehicle tracking', 'command centre', 'command center'] },
  { view: 'lessee', roleIds: [3], keywords: ['lessee', 'applicant', 'quarry', 'miner'] }
];

// ---------------------------------------------------------------------------
// FALLBACK: district master (config API: districts[])
// ---------------------------------------------------------------------------
export const FALLBACK_DISTRICTS: Array<{ id: number; name: string }> = [
  { id: 1, name: 'Tiruvallur' },
  { id: 2, name: 'Chennai' },
  { id: 3, name: 'Kancheepuram' },
  { id: 4, name: 'Vellore' },
  { id: 5, name: 'Dharmapuri' },
  { id: 6, name: 'Tiruvannamalai' },
  { id: 7, name: 'Villupuram' },
  { id: 8, name: 'Salem' },
  { id: 9, name: 'Namakkal' },
  { id: 10, name: 'Erode' },
  { id: 11, name: 'The Nilgiris' },
  { id: 12, name: 'Coimbatore' },
  { id: 13, name: 'Dindigul' },
  { id: 14, name: 'Karur' },
  { id: 15, name: 'Thiruchirappalli' },
  { id: 16, name: 'Perambalur' },
  { id: 17, name: 'Ariyalur' },
  { id: 18, name: 'Cuddalore' },
  { id: 19, name: 'Nagapattinam' },
  { id: 20, name: 'Thiruvarur' },
  { id: 21, name: 'Thanjavur' },
  { id: 22, name: 'Pudukkottai' },
  { id: 23, name: 'Sivagangai' },
  { id: 24, name: 'Madurai' },
  { id: 25, name: 'Theni' },
  { id: 26, name: 'Virudhunagar' },
  { id: 27, name: 'Ramanathapuram' },
  { id: 28, name: 'Thoothukudi' },
  { id: 29, name: 'Tirunelveli' },
  { id: 30, name: 'Kanniyakumari' },
  { id: 31, name: 'Krishnagiri' },
  { id: 32, name: 'Tiruppur' },
  { id: 33, name: 'Chengalpattu' },
  { id: 34, name: 'Kallakurichi' },
  { id: 35, name: 'Ranipet' },
  { id: 36, name: 'Tenkasi' },
  { id: 37, name: 'Tirupattur' },
  { id: 38, name: 'Mayiladuthurai' }
];

/** District code prefixes used in mine/lease codes, mapped to map display names. */
export const FALLBACK_DISTRICT_CODE_PREFIXES: { [prefix: string]: string } = {
  'ARI': 'Ariyalur',
  'CHN': 'Chennai',
  'CBE': 'Coimbatore',
  'CUD': 'Cuddalore',
  'DHA': 'Dharmapuri',
  'DIN': 'Dindigul',
  'ERD': 'Erode',
  'KAL': 'Kallakurichi',
  'KAN': 'Kancheepuram',
  'KKI': 'Kanyakumari',
  'KAR': 'Karur',
  'KRI': 'Krishnagiri',
  'MDU': 'Madurai',
  'MAY': 'Mayiladuthurai',
  'NAG': 'Nagapattinam',
  'NAM': 'Namakkal',
  'NIL': 'Nilgiris',
  'PER': 'Perambalur',
  'PUD': 'Pudukkottai',
  'RAM': 'Ramanathapuram',
  'RAN': 'Ranipet',
  'SAL': 'Salem',
  'SIV': 'Sivaganga',
  'TEN': 'Tenkasi',
  'TNJ': 'Thanjavur',
  'THI': 'Theni',
  'THO': 'Thoothukudi',
  'TIR': 'Tiruchirappalli',
  'TRT': 'Tirupattur',
  'TRU': 'Tiruppur',
  'TVL': 'Tirunelveli',
  'TVR': 'Tiruvarur',
  'TVM': 'Tiruvannamalai',
  'TVE': 'Tiruvallur',
  'VEL': 'Vellore',
  'VIL': 'Viluppuram',
  'VIR': 'Virudhunagar'
};

/** Alternate spellings (lowercase, letters only) mapped to map display names. */
export const FALLBACK_DISTRICT_ALIASES: { [alias: string]: string } = {
  tuticorin: 'Thoothukudi',
  thoothukudi: 'Thoothukudi',
  thoothukkudi: 'Thoothukudi',
  kanchipuram: 'Kancheepuram',
  kancheepuram: 'Kancheepuram',
  thiruvallur: 'Tiruvallur',
  tiruvallur: 'Tiruvallur',
  thiruvarur: 'Tiruvarur',
  tiruvarur: 'Tiruvarur',
  nilgiris: 'The Nilgiris',
  thenilgiris: 'The Nilgiris',
  kanyakumari: 'Kanniyakumari',
  kanniyakumari: 'Kanniyakumari',
  trichy: 'Tiruchirappalli',
  tiruchi: 'Tiruchirappalli',
  thiruchi: 'Tiruchirappalli',
  tiruchirapalli: 'Tiruchirappalli',
  tiruchirappalli: 'Tiruchirappalli',
  thiruchirapalli: 'Tiruchirappalli',
  thiruchirappalli: 'Tiruchirappalli',
  viluppuram: 'Villupuram',
  villupuram: 'Villupuram',
  tirupattur: 'Tirupathur',
  tirupathur: 'Tirupathur',
  thirupathur: 'Tirupathur',
  thiruvannamalai: 'Tiruvannamalai',
  tiruvannamalai: 'Tiruvannamalai',
  pudukottai: 'Pudukkottai',
  pudukkottai: 'Pudukkottai',
  tanjore: 'Thanjavur',
  thanjavur: 'Thanjavur',
  tirunelvelli: 'Tirunelveli',
  tirunelveli: 'Tirunelveli',
  tirupur: 'Tiruppur',
  tiruppur: 'Tiruppur',
  kallakkurichi: 'Kallakurichi',
  kallakurichi: 'Kallakurichi',
  sivagangai: 'Sivaganga',
  sivaganga: 'Sivaganga',
  chengalpet: 'Chengalpattu',
  chengalpattu: 'Chengalpattu',
  ramnad: 'Ramanathapuram',
  ramanathapuram: 'Ramanathapuram'
};

// ---------------------------------------------------------------------------
// FALLBACK: mineral units (config API: minerals[].unit)
// ---------------------------------------------------------------------------
/** Minerals whose name contains one of these keywords are measured in CBM; everything else in MT. */
export const FALLBACK_CBM_MINERAL_KEYWORDS = ['granite'];
export const UNIT_MT = 'MT';
export const UNIT_CBM = 'CBM';
